/* Product cards carry a second photograph that the desktop hover cross-fades
   to. Touch has no hover, so on a phone that image was downloaded and never
   seen. This reveals it as the card first scrolls into view — the same 320ms
   dissolve, on a trigger a thumb can reach — then settles back to the primary
   shot, which is the one chosen to lead.

   Once per card: scrolling back up does not re-fire it, so a long grid stays
   calm rather than flickering every time it passes the fold. */

const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const HOLD_MS = 1100; // how long the alternate shot stays up
const STAGGER_MS = 90; // so a row of cards does not flip in unison

let observer = null;

/* The second shot is lazy-loaded, so on a cold scroll it can still be in
   flight when the card comes into view. Fading the primary out over an image
   that has not arrived leaves the card blank, so wait for the bytes first and
   simply skip the reveal if they never come. */
function whenLoaded(img, run) {
  if (!img) return;
  if (img.complete && img.naturalWidth > 0) {
    run();
    return;
  }
  img.loading = 'eager';
  img.addEventListener('load', run, { once: true });
}

function reveal(media, delay) {
  const alt = media.querySelector('.bwl-card__image--hover');
  window.setTimeout(() => {
    whenLoaded(alt, () => {
      media.classList.add('is-peek');
      window.setTimeout(() => media.classList.remove('is-peek'), HOLD_MS);
    });
  }, delay);
}

function observe(root = document) {
  if (!observer) return;
  root.querySelectorAll('.bwl-card__media--has-hover').forEach((media) => {
    observer.observe(media);
  });
}

function init() {
  // A mouse already gets the hover cross-fade, and reduced motion opts out of
  // the whole effect rather than being handed a slower version of it.
  if (window.matchMedia(FINE_POINTER).matches) return;
  if (window.matchMedia(REDUCED_MOTION).matches) return;
  if (!('IntersectionObserver' in window)) return;

  let shown = 0;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        // Unobserve first: this is a one-shot reveal per card.
        observer.unobserve(entry.target);
        reveal(entry.target, (shown++ % 4) * STAGGER_MS);
      });
    },
    { threshold: 0.55 }
  );

  observe();

  // Collection filtering and paging re-render sections in place, so cards that
  // arrive after load still need picking up.
  document.addEventListener('shopify:section:load', (event) => observe(event.target));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
